/**
 * faq_accordion_controller.js — collapsible landing-page FAQ (Stimulus).
 *
 * Progressive enhancement, SEO-safe by design: the markup ships with every answer
 * OPEN (CSS default), so crawlers, no-JS clients, and the FAQPage JSON-LD all see
 * the full answer text in the initial DOM. On connect() this controller collapses
 * every item except the first and wires the ARIA disclosure semantics; the worst
 * failure mode (no JS) is "all answers visible", never "all answers hidden".
 *
 * Behaviour: single-open accordion. Opening one item collapses its siblings; an
 * open item can be clicked again to collapse it (so all items can be closed).
 *
 * Scoped to the <section class="faq"> element. Stimulus owns the data-action
 * listeners (bound on connect, removed on disconnect), so there is nothing to tear
 * down manually. The height animation lives in CSS (grid-template-rows); the
 * controller only toggles `is-collapsed` and keeps aria-expanded in sync.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            this._items = Array.prototype.slice.call(
                this.element.querySelectorAll('.faq-item')
            );

            this._items.forEach(function (item, index) {
                var button = item.querySelector('.faq-q');
                var panel = item.querySelector('.faq-panel');
                if (!button || !panel) return;

                // Wire ARIA disclosure semantics (ids generated here so the template
                // stays clean and the relationships can never drift out of sync).
                var panelId = panel.id || ('faq-panel-' + index);
                var buttonId = button.id || ('faq-q-' + index);
                panel.id = panelId;
                button.id = buttonId;
                button.setAttribute('aria-controls', panelId);
                panel.setAttribute('role', 'region');
                panel.setAttribute('aria-labelledby', buttonId);

                // First item stays open; collapse the rest.
                if (index === 0) {
                    this._setOpen(item, button, true);
                } else {
                    this._setOpen(item, button, false);
                }
            }, this);

            // Enable the height transition only AFTER the initial collapse so the
            // page doesn't animate items shut on first paint.
            var self = this;
            this._raf = window.requestAnimationFrame(function () {
                self.element.classList.add('faq--enhanced');
            });
        }

        disconnect() {
            if (this._raf) { window.cancelAnimationFrame(this._raf); this._raf = null; }
        }

        toggle(event) {
            var button = event.currentTarget;
            var item = button.closest('.faq-item');
            if (!item) return;

            var willOpen = item.classList.contains('is-collapsed');
            if (willOpen) {
                // Single-open: collapse every other item, then open this one.
                this._items.forEach(function (other) {
                    if (other !== item) {
                        this._setOpen(other, other.querySelector('.faq-q'), false);
                    }
                }, this);
            }
            this._setOpen(item, button, willOpen);
        }

        _setOpen(item, button, open) {
            item.classList.toggle('is-collapsed', !open);
            if (button) button.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
}
